import { useAuth } from '../contexts/AuthContext';
import { ROLES, PERMISSIONS, PERMISSION_MAP, normalizeRole, type Role, type Permission } from '../../constants/roles';
import type { DocumentType } from '../../types/Document';

/**
 * Permission Hook for Role-Based Access Control (RBAC)
 *
 * Provides functions to check if the current user has specific permissions
 * based on their role. Used to conditionally render UI elements and control
 * access to features.
 *
 * @example
 * ```tsx
 * const { canInviteUsers, canModifyFoundationDocs } = usePermissions();
 *
 * {canInviteUsers() && (
 *   <Button onClick={handleInvite}>Invite User</Button>
 * )}
 * ```
 */
export function usePermissions() {
  const { user } = useAuth();

  /**
   * Check if the current user has a specific permission
   * @param permission - Permission to check (e.g., PERMISSIONS.USERS_INVITE)
   * @returns true if user has permission, false otherwise
   */
  const hasPermission = (permission: Permission): boolean => {
    if (!user?.role) {
      return false;
    }

    // Normalize so a stale legacy-role JWT (e.g. marketing_manager) resolves
    // to a canonical role instead of an undefined PERMISSION_MAP lookup.
    const role = normalizeRole(user.role);

    // Owner has all permissions (wildcard)
    if (role === ROLES.OWNER) {
      return true;
    }

    // Check if role's permission list includes the requested permission
    const rolePermissions = PERMISSION_MAP[role];
    if (!rolePermissions) {
      return false;
    }

    // Check for wildcard or specific permission
    // Cast to readonly array to satisfy TypeScript when dealing with union types
    const permissions = rolePermissions as readonly (Permission | '*')[];
    return permissions.includes('*') || permissions.includes(permission);
  };

  /**
   * Check if current user can invite users
   * @returns true if user can invite users (Owner only)
   */
  const canInviteUsers = (): boolean => {
    return hasPermission(PERMISSIONS.USERS_INVITE);
  };

  /**
   * Check if current user can manage users (roles, permissions, removal)
   * @returns true if user can manage users (Owner only)
   */
  const canManageUsers = (): boolean => {
    return hasPermission(PERMISSIONS.USERS_MANAGE);
  };

  /**
   * Check if current user can modify foundation documents
   * @returns true if user can modify foundation documents (Owner only)
   */
  const canModifyFoundationDocs = (): boolean => {
    return hasPermission(PERMISSIONS.DOCUMENTS_MODIFY_FOUNDATION);
  };

  /**
   * Check if current user can execute onboarding workflows
   * @returns true if user can execute onboarding workflows (Owner, Member)
   */
  const canExecuteOnboardingWorkflows = (): boolean => {
    return hasPermission(PERMISSIONS.WORKFLOWS_EXECUTE_ONBOARDING);
  };

  /**
   * Check if current user can execute workflows
   * @returns true if user can execute workflows (Owner, Member)
   */
  const canExecuteWorkflows = (): boolean => {
    return hasPermission(PERMISSIONS.WORKFLOWS_EXECUTE);
  };

  /**
   * Check if current user can create campaigns
   * @returns true if user can create campaigns (Owner, Member)
   */
  const canCreateCampaigns = (): boolean => {
    return hasPermission(PERMISSIONS.CAMPAIGNS_CREATE);
  };

  /**
   * Check if current user can edit campaigns
   * @returns true if user can edit campaigns (Owner, Member)
   */
  const canEditCampaigns = (): boolean => {
    return hasPermission(PERMISSIONS.CAMPAIGNS_EDIT);
  };

  /**
   * Check if current user can create content
   * @returns true if user can create content (Owner, Member)
   */
  const canCreateContent = (): boolean => {
    return hasPermission(PERMISSIONS.CONTENT_CREATE);
  };

  /**
   * Check if current user can edit content
   * @returns true if user can edit content (Owner, Member)
   */
  const canEditContent = (): boolean => {
    return hasPermission(PERMISSIONS.CONTENT_EDIT);
  };

  /**
   * Check if current user can view calendar
   * @returns true if user can view calendar (Owner, Member)
   */
  const canViewCalendar = (): boolean => {
    return hasPermission(PERMISSIONS.CALENDAR_VIEW);
  };

  /**
   * Check if current user can view analytics
   * @returns true if user can view analytics (Owner, Member, Viewer)
   */
  const canViewAnalytics = (): boolean => {
    return hasPermission(PERMISSIONS.ANALYTICS_VIEW);
  };

  /**
   * Check if current user can read documents
   * @returns true if user can read documents (all roles)
   */
  const canReadDocuments = (): boolean => {
    return hasPermission(PERMISSIONS.DOCUMENTS_READ);
  };

  /**
   * Check if current user can edit a specific document type
   * Foundation docs (audit, persona, brand, strategy): Owner only
   * Campaign/content docs: Owner, Member
   * @param documentType - Type of document to check
   * @returns true if user can edit the document type
   */
  const canEditDocument = (documentType: DocumentType): boolean => {
    const foundationTypes: DocumentType[] = ['audit', 'persona', 'brand', 'strategy'];
    const isFoundationDoc = foundationTypes.includes(documentType);

    if (isFoundationDoc) {
      return canModifyFoundationDocs();
    }

    // Campaign and content docs can be edited by Owner, Member
    return canEditContent();
  };

  /**
   * Check if current user can view dashboards
   * @returns true if user can view dashboards (all roles)
   */
  const canViewDashboards = (): boolean => {
    return hasPermission(PERMISSIONS.DASHBOARDS_VIEW);
  };

  /**
   * Check if current user can curate dashboards (create / rename / archive
   * boards, remove / reorder items) — Epic 8 story 8.3.
   * @returns true for Owner and Member (the harness `dashboards.manage` verb)
   */
  const canManageDashboards = (): boolean => {
    return hasPermission(PERMISSIONS.DASHBOARDS_MANAGE);
  };

  /**
   * Get current user's role
   * @returns User's role or null if not authenticated
   */
  const getUserRole = (): Role | null => {
    // Normalize so a stale legacy role (marketing_manager / content_creator)
    // resolves to its canonical role (member) rather than leaking through as
    // an unrecognized string. Unauthenticated → null.
    return user?.role ? normalizeRole(user.role) : null;
  };

  /**
   * Check if current user has a specific role
   * @param role - Role to check (e.g., ROLES.OWNER)
   * @returns true if user has the specified role
   */
  const hasRole = (role: Role): boolean => {
    // Normalize the (possibly stale legacy) claim so isOwner()/isMember()/
    // isViewer() classify a migrated user correctly. Unauthenticated → false.
    return user?.role ? normalizeRole(user.role) === role : false;
  };

  /**
   * Check if current user is an Owner — the ratified owner-or-superuser gate.
   *
   * THE SINGLE FRONTEND FOLD of the ratified RBAC rule (Paul, 2026-05-27):
   * `is_superuser` IMPLIES owner (T-20260916-1).
   *
   * 🔴 USE `isOwnerByRole()` INSTEAD WHERE THE SERVER VERB IS ROLE-PURE.
   * The harness's `permissions/can.ts` is deliberately claim-only — its header
   * states "Superuser does NOT bypass these gates (no DB hop per write); the
   * JWT role claim is the sole input" — so verbs like `documents.delete`
   * (`can.ts:89`), `secrets.manage` (`can.ts:138`) and `tasks.reassign`
   * (`can.ts:259`) refuse a superuser whose tenant role is `member`/`viewer`.
   * Rendering a control off `isOwner()` at one of those sites would offer a
   * button the server answers with 403, which the repo refuses to do
   * (`ActionBar.tsx:236`). The split is `can.ts`'s own principle applied on the
   * client, not a new policy.
   *
   * THE CONSUMERS, and which predicate each one takes. (Six on `isOwner()` — 1, 2, 2b, 3, 4 and 4a —
   * ELEVEN on `isOwnerByRole()` — T-20260916-2 added six of the latter when
   * every Admin row became owner-only, T-20260916-8 added the twelfth, and
   * DW-480 retired item 5 to leave eleven.
   * The split, not the count, is the point; nothing asserts this list, so treat
   * it as a map and re-grep before trusting a line number.)
   *   `isOwner()` — the server gate folds superuser in, so the SPA must too:
   *     1. `components/features/auth/RequireOwner.tsx:58` — every `/admin/*`
   *        route except the role-open `/admin/agents`, plus `/import/csv`,
   *        `/settings/team` (the team page, moved from `/settings` by Story
   *        24.8) and `/settings/plugins` since T-20260916-2; the harness
   *        counterpart is `requireOwnerRole`, widened. 🔴 Those last three
   *        pages then NARROW again at their own page gate below — the route
   *        admits a superuser member and the page refuses them, deliberately,
   *        because their writes are role-pure. `/settings` itself is the
   *        role-open Settings hub and has no guard (ruled M-2).
   *     2. `components/layout/Sidebar.tsx` — its only use is the `isOwner`
   *        field of the viewer it passes to the superuser-only Engineering
   *        group (`superAdminNavigation`, all `superuserOnly`). Since Story
   *        24.8 its Settings row is role-open (`settingsNavRow`); the
   *        owner-only `adminNavigation` rows are drawn by the hub (2b).
   *        (Former 2a, `pages/MorePage.tsx`, no longer calls `isOwner()`:
   *        its Settings row is role-open since Story 24.8.)
   *     2b. Story 24.8 — `pages/SettingsHubPage.tsx`, the Settings hub. Its
   *        viewer filters `adminNavigation`'s `ownerOnly` cards and links,
   *        which lead to `<RequireOwner>` routes (1) — so it takes the same
   *        widened predicate as those routes.
   *     3. `pages/ScheduledTasksSettingsPage.tsx:8` — the whole page; its
   *        writes hit `routes/scheduled-jobs.ts`, all eight endpoints gated by
   *        the widened `requireOwnerRole`.
   *     4. `pages/MarketplacePage.tsx` — the plugin enable/disable TOGGLE
   *        (not the page: browsing the catalogue is role-open). Its server
   *        gate is `createRequireOwnerRole` on
   *        `POST /api/plugins/:id/{enable,disable}`, widened, so narrowing
   *        here would HIDE a control for a call the harness accepts — and
   *        would break the A8 flow, which exists for a superuser whose
   *        tenant role is not owner (T-20260916-2). The most instructive
   *        entry in this list: it sits in the same task as items 10-14 and
   *        takes the OTHER predicate, because the rule is "match the
   *        server", not "pick one and be consistent".
   *     4a. Story 16.10 — `lib/hooks/useCreditExhaustedAction.ts` (the
   *        credit-exhausted "View billing" action: chat turn banner,
   *        task-thread banner and failure post, voice toast) and
   *        `components/features/billing/OwnerLedgerHistory.tsx` ("Credit
   *        history"); like 16.4's `CreditBalanceBanner`, they match
   *        `<RequireOwner>` on Usage & LLM Cost and `requireOwnerRole` on
   *        `GET /api/billing/ledger`. (Add credit stays `isOwnerByRole()` —
   *        the checkout verb is role-pure.)
   *   `isOwnerByRole()` — the server verb is role-pure, or the narrowing is a
   *   deliberate local safety decision:
   *     5. (retired) — was the delete affordance in the orphaned
   *        `DocumentList` component, deleted as dead code by DW-480; the
   *        number is kept so "items 5-17" below stays correct.
   *     6. `pages/DocumentDetailPage.tsx:564` — the Delete button; verb
   *        `documents.delete` is `['owner']`.
   *     7. `pages/IntegrationsSettingsPage.tsx:140` — the page gate; its writes
   *        hit `secrets.manage`, `['owner']`.
   *     8. `components/features/assignments/AssigneeSelect.tsx:37` — with
   *        `isMember()`; verb `tasks.reassign` is `['owner','member']`, so a
   *        superuser VIEWER must not see it.
   *     9. `pages/admin/AgentDetailPage.tsx:178` — the skill Archive button.
   *        The route DOES admit a superuser; the button deliberately does not
   *        (destructive, one-way, on someone else's tenant). See that file.
   *    10. `pages/SettingsPage.tsx` — the page gate; every write hits
   *        `users.manage`, `['owner']` (T-20260916-2).
   *    11. `pages/CSVImportPage.tsx` — the page gate; the wizard's writes
   *        carry `requirePermission('ingest.write')`, role-pure (T-20260916-2).
   *    12. `pages/PluginSettingsHostPage.tsx` — the page gate; `secrets.manage`
   *        plus the owner-gated plugin lifecycle routes (T-20260916-2) and,
   *        since T-20260916-10, the owner-gated
   *        `PATCH /api/plugins/:id/settings`. (The DangerZone reset entry point
   *        that used to be item 13 was retired by T-20260916-10: its endpoints
   *        were served by nothing.)
   *    13. (retired by Epic 8 story 8.5: the plugin-dashboard empty state that
   *        held a LINK affordance here was deleted with the N8N widget path.)
   *    14. `components/features/plugin/schedule-dialog/NoAccountsConnected.tsx`
   *        — a LINK affordance, not a gate: it points at a page that gates on
   *        this same predicate, and a link offered to someone the destination
   *        refuses is a dead-end (T-20260916-2).
   *    15. `pages/ApiKeysSettingsPage.tsx` — the page gate; all three
   *        `/api/api-keys` routes carry `requirePermission('api_keys.manage')`,
   *        which `can.ts:142` maps to `['owner']` (T-20260916-8). Same shape as
   *        item 7: a `<RequireOwner>` route that ADMITS a superuser member, and
   *        a page that then refuses them — because otherwise they would hold a
   *        fully rendered mint/rotate/revoke form whose every request 403s.
   *        Pinned, unlike most of this list: that page's suite drives the two
   *        predicates independently so widening its gate fails a test.
   *    16. `components/features/calendar/CalendarSchedulePanel.tsx` — the
   *        Delete Item button (disabled, with the reason, for anyone else);
   *        `DELETE /api/calendar/:id` carries `calendar.delete`, `['owner']`
   *        (story 12.3). Pinned in that panel's suite.
   *    17. `components/features/billing/AddCreditPanel.tsx` — the whole panel;
   *        `POST /api/billing/checkout` is owner-only and role-pure (story
   *        16.7, B5), so a superuser member gets no panel. Its return page,
   *        `pages/billing/CheckoutSuccessPage.tsx`, narrows the same way under a
   *        `<RequireOwner>` route (the status route is role-pure too). Pinned
   *        in the panel's suite.
   *
   * The converse is NEVER implied: `superuserOnly` rows stay superuser-only and
   * keep reading `user.is_superuser` directly, so an owner who is not a
   * superuser still does not see them. Since T-20260916-3 there are THREE of
   * them, not two — Byte Cost joined Prompt Management and Billing & Credits —
   * and all three now live in `superAdminNavigation` under the SuperAdmin
   * submenu rather than in `adminNavigation`. Read the count off the array, not
   * off this sentence.
   *
   * `hasRole()` deliberately stays a STRICT role predicate — folding superuser
   * in there would make `isMember()` / `isViewer()` lie about a superuser's
   * actual tenant role, and `getUserRole()` reports the real role too.
   *
   * Coercion matches the harness helper `permissions/is-superuser.ts`: ONLY a
   * literal `true` counts, so `'true'`, `1`, `{}` and `null` are not ownership.
   *
   * @returns true if the user holds the Owner role OR is a platform superuser
   */
  const isOwner = (): boolean => {
    return hasRole(ROLES.OWNER) || user?.is_superuser === true;
  };

  /**
   * Check if current user holds the Owner ROLE — superuser is NOT folded in.
   *
   * The counterpart to `isOwner()`, and the one to reach for whenever the
   * affordance being gated maps to a `can.ts` verb, because that map reads the
   * JWT role claim and nothing else. Named rather than inlined as
   * `hasRole(ROLES.OWNER)` at the call site so the choice between the two gates
   * is visible in a diff and cannot be "tidied" back onto `isOwner()`.
   *
   * Its consumers are items 5-17 in the list above: the ones whose server verb
   * is role-pure, plus the sites where the route WOULD admit a superuser and
   * the surface deliberately does not (`AgentDetailPage`'s Archive button,
   * and the three Admin-menu pages T-20260916-2 gated, which
   * sit behind a `RequireOwner` route that admits one).
   *
   * @returns true only if the user's (normalized) tenant role is Owner
   */
  const isOwnerByRole = (): boolean => {
    return hasRole(ROLES.OWNER);
  };

  /**
   * Check if current user is a Member (the non-owner working role)
   * @returns true if user is a Member
   */
  const isMember = (): boolean => {
    return hasRole(ROLES.MEMBER);
  };

  /**
   * Check if current user is a Viewer
   * @returns true if user is a Viewer
   */
  const isViewer = (): boolean => {
    return hasRole(ROLES.VIEWER);
  };

  return {
    // Core permission checking
    hasPermission,

    // User management
    canInviteUsers,
    canManageUsers,

    // Document permissions
    canModifyFoundationDocs,
    canReadDocuments,
    canEditDocument,

    // Workflow permissions
    canExecuteOnboardingWorkflows,
    canExecuteWorkflows,

    // Campaign permissions
    canCreateCampaigns,
    canEditCampaigns,

    // Content permissions
    canCreateContent,
    canEditContent,

    // View permissions
    canViewCalendar,
    canViewAnalytics,
    canViewDashboards,
    canManageDashboards,

    // Role checking
    getUserRole,
    hasRole,
    isOwner,
    isOwnerByRole,
    isMember,
    isViewer,
  };
}
